import { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../auth/AuthContext';
let version = 0;
let foregroundStatus = 'Checking foreground notifications…';
const statusEvent = 'aeonas-foreground-status';
function status(text) {
  foregroundStatus = text;
  window.dispatchEvent(new Event(statusEvent));
}
export function useChatPresenceStatus() {
  const [value, setValue] = useState(foregroundStatus);
  useEffect(() => {
    const changed = () => setValue(foregroundStatus);
    window.addEventListener(statusEvent, changed);
    changed();
    return () => window.removeEventListener(statusEvent, changed);
  }, []);
  return value;
}
function nextVersion() {
  version = Math.max(Date.now(), version + 1);
  return version;
}
function bounded(promise) {
  let timer;
  return Promise.race([promise, new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('Foreground registration timed out. Retrying…')), 8000);
  })]).finally(() => clearTimeout(timer));
}
let fallbackId;
function clientId(scope) {
  let id;
  try { id = sessionStorage.getItem('aeonas-chat-client'); } catch (_) {}
  if (!id) {
    id = fallbackId ||= (window.crypto?.randomUUID?.() || 'chat_' + Date.now() + '_' + Math.random().toString(36).slice(2));
    try { sessionStorage.setItem('aeonas-chat-client', id); } catch (_) {}
  }
  return id + '_' + scope;
}
export default function useChatPresence(params, mobileOpen) {
  const { user } = useAuth();
  useEffect(() => {
    if (!user?.id || (params.channel === 'dm' && !params.peer)) return undefined;
    const app = params.channel === 'app';
    const id = clientId(app ? 'app' : 'conversation');
    let alive = true, endpoint = null, pending = false;
    let registration = null;
    const active = () => document.visibilityState === 'visible' && (app || window.innerWidth > 720 || mobileOpen);
    const report = async () => {
      if (!alive || pending || !active()) return;
      pending = true;
      try {
        if (!navigator.serviceWorker) throw new Error('Push service unavailable in this app.');
        if (!endpoint) {
          // Discover and activate the worker from every app view.
          registration ||= await bounded(navigator.serviceWorker.register('/aeonas-push-sw.js', { scope: '/', updateViaCache: 'none' }));
          if (!registration.active) registration = await bounded(navigator.serviceWorker.ready);
          const manager = registration.pushManager || window.pushManager;
          const sub = await bounded(manager?.getSubscription());
          if (!sub?.endpoint) throw new Error('No push subscription in this app. Enable push in Chat.');
          endpoint = sub.endpoint;
        }
        if (!alive || !active()) return;
        const response = await api.post('/api/chat/push/presence', {
          ...params, endpoint, clientId: id, version: nextVersion(), active: true
        }, { timeout: 8000 });
        if (response.data?.registered !== true) throw new Error('Server has not confirmed foreground registration.');
        if (app && alive && active()) status('Foreground notifications: sound only');
      } catch (e) {
        if (app && alive && active()) status(e.response?.data?.message || e.message || 'Foreground registration failed.');
      } finally { pending = false; }
    };
    const clear = () => {
      if (app) status('App in background: push notifications enabled');
      if (!endpoint) return;
      const body = JSON.stringify({ ...params, endpoint, clientId: id, version: nextVersion(), active: false });
      let sent = false;
      try { sent = navigator.sendBeacon?.('/api/chat/push/presence', new Blob([body], { type: 'application/json' })) || false; } catch (_) {}
      if (!sent) void fetch('/api/chat/push/presence', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body, keepalive: true
      }).catch(() => {});
    };
    const change = () => { if (active()) void report(); else clear(); };
    const subscriptionChanged = () => { endpoint = null; registration = null; change(); };
    if (app) status('Checking foreground notifications…');
    change();
    const timer = setInterval(change, 3000);
    document.addEventListener('visibilitychange', change);
    window.addEventListener('focus', change);
    window.addEventListener('pageshow', change);
    window.addEventListener('resize', change);
    window.addEventListener('pagehide', clear);
    window.addEventListener('aeonas-push-changed', subscriptionChanged);
    navigator.serviceWorker?.addEventListener('controllerchange', subscriptionChanged);
    return () => {
      alive = false;
      clearInterval(timer);
      clear();
      document.removeEventListener('visibilitychange', change);
      window.removeEventListener('focus', change);
      window.removeEventListener('pageshow', change);
      window.removeEventListener('resize', change);
      window.removeEventListener('pagehide', clear);
      window.removeEventListener('aeonas-push-changed', subscriptionChanged);
      navigator.serviceWorker?.removeEventListener('controllerchange', subscriptionChanged);
    };
  }, [params, mobileOpen, user?.id]);
}
